import { TeaserData } from "@app/types/ServerCustomContext";
import { getVariantByIndex } from "@app/utils/VariantByIndex";
import AppExploreMore, {
  AppExploreMoreProps,
} from "@components/Modules/AppExploreMore/AppExploreMore";
import {
  configCategory,
  configExploreMore,
  configLatestStory,
  configTeaser,
} from "@pub/config";
import placeholders from "@pub/placeholders";
import {
  Ads,
  Card,
  Divider,
  LatestArticle,
  SectionContext,
  SponsorshipBox,
  useCustomClassContext,
  UserSubmissionBox,
} from "@sphtech/dmg-design-system";
import cx from "classnames";
import { ReactNode } from "react";

import styles from "./category.module.css";

type Props = {
  data: {
    exploreMore?: AppExploreMoreProps;
    mainContentBlock?: TeaserData[];
    lb1?: ReactNode;
    imu?: ReactNode;
    userSubmissionBox?: UserSubmissionBox;
  };
};

export default function CategoryStoryListingLayout({ data }: Props): ReactNode {
  const categoryLayout = configCategory.card?.latestArticle;
  const customClass = useCustomClassContext();
  const mainContentBlockMode =
    configCategory.latestStory?.mainContentBlockMode ||
    configLatestStory.mainContentBlockMode;

  if (data.mainContentBlock && data.mainContentBlock.length > 0) {
    const ExploreMore = data.exploreMore && (
      <SectionContext.Provider value={{}}>
        <AppExploreMore
          {...data.exploreMore}
          headingText={configExploreMore.heading}
          headingTextZH={configExploreMore.headingZH}
          render={(children) => (
            <div
              className={cx(
                styles.exploreMoreContainer,
                customClass?.categoryLayout?.exploreMoreContainer,
              )}
            >
              {!configExploreMore.hideDivider && <Divider />}
              {children}
              {!configExploreMore.hideDivider && <Divider />}
            </div>
          )}
        />
      </SectionContext.Provider>
    );
    return (
      <section className={cx(styles.bodyContainer, styles.gutterBottom)}>
        <div className={styles.mainContentBlockContainer}>
          {/* Contains list of LatestArticle */}
          <div
            className={cx(
              styles.mainContentBlockLayout,
              styles[configLatestStory.mainContentBlockDisplay],
            )}
          >
            {data.userSubmissionBox && (
              <div className={styles.userSubmissionBox}>
                {
                  <SponsorshipBox
                    inPage={true}
                    {...data.userSubmissionBox}
                    buttonType="primary"
                  />
                }
              </div>
            )}
            {data.mainContentBlock.map((teaser, index) => (
              <div
                key={index}
                className={cx(
                  styles.mainContentBlockItem,
                  styles[mainContentBlockMode],
                  customClass?.categoryLayout?.mainContent?.item,
                )}
              >
                {categoryLayout ? (
                  <Card
                    key={index}
                    {...teaser}
                    variant={getVariantByIndex(categoryLayout, index)}
                    mobileUpVariant={getVariantByIndex(
                      categoryLayout.mobileUp,
                      index,
                    )}
                    placeholders={placeholders}
                    displayPublishDate={configTeaser.publishDate}
                    CustomHeadingElement="h2"
                    dateAfter={configTeaser.dateAfter}
                  />
                ) : (
                  <LatestArticle
                    key={index}
                    {...teaser}
                    mode={mainContentBlockMode}
                    portraitImageWidth={configLatestStory.portraitImageWidth}
                    landscapeImageWidth={configLatestStory.landscapeImageWidth}
                    placeholders={placeholders}
                    displayPublishDate={configTeaser.publishDate}
                    mobileMode={configTeaser.mobileMode}
                    CustomHeadingElement="h2"
                    dateAfter={configTeaser.dateAfter}
                  />
                )}

                {index === configCategory.exploreMorePosition && (
                  <div className={styles.exploreMorePosition}>
                    {ExploreMore}
                  </div>
                )}

                {index === configCategory.adPositioning && (
                  <div className={styles.mobileAdvertisement}>
                    <Ads.AdsSideBarMobile />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contains Ads */}
          <div
            className={cx(
              styles.sidebar,
              typeof configCategory.exploreMorePosition === "number" &&
                styles.exploreMoreSideBar,
            )}
          >
            {data.exploreMore && ExploreMore}
            {configCategory.adPositioning ? (
              <>
                <Ads.AdsSideBarDesktop />
                {!data.exploreMore && configCategory.hasDoubleSideBarAds && (
                  <Ads.AdsSideBarDesktop />
                )}
              </>
            ) : (
              <Ads.AdsSideBar />
            )}
          </div>
        </div>
      </section>
    );
  }
}
